import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Circle, Polygon, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Notams.css';
import { getAccessToken } from '../utils/auth';
import { getActiveNotams, loadInitialNotams } from '../utils/httpApi';
import { getTileLayerConfig, getMapFilter } from '../utils/mapStyles';

// ─── NOTAM category colors (no purple — use teal for 'other') ───
const NOTAM_COLORS = {
  closure:  { fill: '#e74c3c', stroke: '#c0392b', label: 'Closure'  },
  obstacle: { fill: '#FF6B35', stroke: '#cc5529', label: 'Obstacle' },
  airspace: { fill: '#D4A520', stroke: '#b8901a', label: 'Airspace' },
  navaid:   { fill: '#3b9dff', stroke: '#2b7acc', label: 'Navaid'   },
  other:    { fill: '#20c997', stroke: '#1aa37a', label: 'Other'    },
};

// ─── Classification filters ───
const CLASSIFICATION_FILTERS = ['ALL', 'DOM', 'MIL', 'INTL', 'FDC'];

// ─── ICAO prefix → country mapping ───
const ICAO_COUNTRY_MAP = {
  'K':'United States','PA':'United States','PH':'United States','PG':'United States',
  'TJ':'United States','PJ':'United States',
  'C':'Canada',
  'MM':'Mexico','MG':'Guatemala','MH':'Honduras','MK':'Jamaica','MN':'Nicaragua',
  'MP':'Panama','MR':'Costa Rica','MS':'El Salvador','MT':'Haiti','MU':'Cuba',
  'MD':'Dominican Republic','MZ':'Belize','MY':'Bahamas',
  'EG':'United Kingdom','EI':'Ireland','ED':'Germany','ET':'Germany',
  'EH':'Netherlands','EB':'Belgium','EL':'Luxembourg','EF':'Finland',
  'EN':'Norway','ES':'Sweden','EK':'Denmark','EP':'Poland',
  'EV':'Latvia','EY':'Lithuania','EE':'Estonia','BI':'Iceland',
  'LF':'France','LI':'Italy','LE':'Spain','LP':'Portugal','LO':'Austria',
  'LS':'Switzerland','LK':'Czech Republic','LZ':'Slovakia','LH':'Hungary',
  'LR':'Romania','LB':'Bulgaria','LG':'Greece','LT':'Turkey','LC':'Cyprus',
  'LW':'North Macedonia','LQ':'Bosnia','LD':'Croatia','LJ':'Slovenia',
  'LY':'Serbia','LU':'Moldova','GC':'Canary Islands','GM':'Morocco',
  'UK':'Ukraine','UM':'Belarus',
  'UL':'Russia','UU':'Russia','UR':'Russia','UW':'Russia','UE':'Russia','UN':'Russia',
  'OA':'Afghanistan','OB':'Bahrain','OE':'Saudi Arabia','OI':'Iran','OJ':'Jordan',
  'OK':'Kuwait','OL':'Lebanon','OM':'UAE','OO':'Oman','OP':'Pakistan',
  'OR':'Iraq','OS':'Syria','OT':'Qatar','OY':'Yemen','LL':'Israel',
  'VT':'Thailand','VV':'Vietnam','VL':'Laos','VD':'Cambodia',
  'VH':'Hong Kong','VM':'Macau',
  'WA':'Indonesia','WI':'Indonesia','WB':'Malaysia','WM':'Malaysia','WS':'Singapore',
  'RP':'Philippines','RK':'South Korea','RJ':'Japan','RO':'Japan','RC':'Taiwan',
  'Z':'China','VE':'India','VI':'India','VO':'India','VA':'India',
  'Y':'Australia','NZ':'New Zealand','NF':'Fiji',
  'SA':'Argentina','SB':'Brazil','SC':'Chile','SE':'Ecuador','SK':'Colombia',
  'SL':'Bolivia','SP':'Peru','SU':'Uruguay','SV':'Venezuela',
  'DA':'Algeria','DN':'Nigeria','DT':'Tunisia',
  'FA':'South Africa','FV':'Zimbabwe','FY':'Namibia',
  'HA':'Ethiopia','HE':'Egypt','HK':'Kenya','HL':'Libya','HS':'Sudan',
  'HT':'Tanzania','HU':'Uganda',
  'TT':'Trinidad and Tobago','TX':'Bermuda',
  'BG':'Greenland',
};

/** Get country from ICAO location code */
function getCountryFromIcao(icao) {
  if (!icao) return null;
  const upper = icao.toUpperCase();
  // Try 2-char prefix first (more specific), then 1-char
  return ICAO_COUNTRY_MAP[upper.substring(0, 2)] ||
         ICAO_COUNTRY_MAP[upper.substring(0, 1)] ||
         null;
}

/** Get region/FIR for sub-country filtering */
function getRegion(notam) {
  return notam.affectedFir || null;
}

/** Classify NOTAM for badge display */
function classifyNotam(notam) {
  const code = (notam.selectionCode || '').toUpperCase();
  const text = (notam.notamText || '').toUpperCase();
  if (code.startsWith('QMR') || code.startsWith('QMX') || code.startsWith('QMA') ||
      code.startsWith('QFAL') || text.includes('CLSD') || text.includes('CLOSED')) return 'closure';
  if (code.startsWith('QOB') || code.startsWith('QOL') || text.includes('OBST') || text.includes('CRANE')) return 'obstacle';
  if (code.startsWith('QR') || code.startsWith('QA') || text.includes('TFR') ||
      text.includes('RESTRICTED') || text.includes('PROHIBITED')) return 'airspace';
  if (code.startsWith('QN') || text.includes('VOR') || text.includes('ILS') || text.includes('DME')) return 'navaid';
  return 'other';
}

/** Get NOTAM type label */
function getTypeLabel(type) {
  switch (type) {
    case 'N': return 'New';
    case 'R': return 'Replace';
    case 'C': return 'Cancel';
    default: return type || '-';
  }
}

/** Format datetime for display */
function formatDate(dt) {
  if (!dt) return '-';
  const d = Array.isArray(dt) ? new Date(dt[0], dt[1] - 1, dt[2], dt[3] || 0, dt[4] || 0) : new Date(dt);
  if (isNaN(d.getTime())) return dt;
  return d.toISOString().replace('T', ' ').substring(0, 16) + 'Z';
}

/** Create marker icon for a NOTAM category */
function createNotamIcon(category, isSelected) {
  const colors = NOTAM_COLORS[category] || NOTAM_COLORS.other;
  const size = isSelected ? 14 : 10;
  const border = isSelected ? 3 : 2;
  const shadow = isSelected ? '0 0 8px rgba(255,255,255,0.6)' : '0 1px 3px rgba(0,0,0,0.4)';
  return L.divIcon({
    className: 'notam-map-icon',
    html: `<div style="
      background:${colors.fill};
      border:${border}px solid ${isSelected ? '#fff' : colors.stroke};
      border-radius:50%;
      width:${size}px;height:${size}px;
      box-shadow:${shadow};
    "></div>`,
    iconSize: [size + border * 2, size + border * 2],
    iconAnchor: [(size + border * 2) / 2, (size + border * 2) / 2]
  });
}

/** Parse polygon coordinates */
function parsePolygon(polygonStr) {
  if (!polygonStr) return null;
  try {
    if (polygonStr.startsWith('[')) {
      const arr = JSON.parse(polygonStr);
      if (Array.isArray(arr) && arr.length >= 3) return arr;
    }
    const parts = polygonStr.trim().split(/\s+/).map(Number);
    if (parts.length >= 6 && parts.every(n => !isNaN(n))) {
      const coords = [];
      for (let i = 0; i < parts.length - 1; i += 2) {
        coords.push([parts[i], parts[i + 1]]);
      }
      if (coords.length >= 3) return coords;
    }
  } catch (e) { /* not parseable */ }
  return null;
}


// ─── Map auto-fit component ───
function MapFitter({ notams, selectedNotam, filterKey }) {
  const map = useMap();
  const fittedRef = useRef(false);
  const lastFilterKeyRef = useRef(null);

  // When selected NOTAM changes, zoom to it
  useEffect(() => {
    if (selectedNotam?.centerLatitude != null && selectedNotam?.centerLongitude != null) {
      map.setView([selectedNotam.centerLatitude, selectedNotam.centerLongitude],
        Math.max(map.getZoom(), 8), { animate: true });
    }
  }, [map, selectedNotam]);

  // Fit bounds when filter changes (country/region) or on initial load
  useEffect(() => {
    const isFilterChange = filterKey !== lastFilterKeyRef.current;
    lastFilterKeyRef.current = filterKey;

    if ((!fittedRef.current || isFilterChange) && notams.length > 0 && !selectedNotam) {
      const withCoords = notams.filter(n => n.centerLatitude != null && n.centerLongitude != null);
      if (withCoords.length > 0) {
        const bounds = L.latLngBounds(withCoords.map(n => [n.centerLatitude, n.centerLongitude]));
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 12, animate: true });
        fittedRef.current = true;
      }
    }
  }, [map, notams, filterKey, selectedNotam]);

  return null;
}


// ─── Main component ───
function Notams({ notamUpdates = [], userRoles = [] }) {
  const [notams, setNotams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNotam, setSelectedNotam] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [classFilter, setClassFilter] = useState('ALL');
  const [initialLoadStatus, setInitialLoadStatus] = useState(null);

  // Category type toggles (all on by default)
  const [categoryToggles, setCategoryToggles] = useState({
    closure: true, obstacle: true, airspace: true, navaid: true, other: true
  });

  // Country / region filters
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [regionFilter, setRegionFilter] = useState('ALL');

  const isAdmin = userRoles.includes('ADMIN');
  const listRef = useRef(null);

  // Tile layer config
  const tileConfig = useMemo(() => getTileLayerConfig(), []);
  const mapFilter = useMemo(() => getMapFilter(tileConfig.brightness), [tileConfig]);

  // ─── Fetch NOTAMs ───
  // `background` refreshes keep the current list on screen instead of blanking it.
  const fetchNotams = useCallback(async ({ background = false } = {}) => {
    const token = getAccessToken();
    if (!token) return;
    if (!background) setLoading(true);
    try {
      const filters = {};
      if (classFilter !== 'ALL') filters.classification = classFilter;
      const response = await getActiveNotams(filters, token);
      if (response.header?.apiStatus === 'SUCCESS' || response.header?.apiStatus === 'OK') {
        setNotams(response.notams || []);
      }
    } catch (error) {
      console.error('Error fetching NOTAMs:', error);
    } finally {
      setLoading(false);
    }
  }, [classFilter]);

  const handleInitialLoad = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    setInitialLoadStatus('loading');
    try {
      const response = await loadInitialNotams(60, token);
      if (response.header?.apiStatus === 'OK') {
        setInitialLoadStatus(`Loaded ${response.messagesInserted} NOTAMs (${response.messagesDuplicate} dup, ${response.messagesFailed} fail) in ${(response.durationMs / 1000).toFixed(1)}s`);
        fetchNotams();
      } else {
        setInitialLoadStatus('Error: ' + (response.errors?.[0]?.message || 'Unknown'));
      }
    } catch (error) {
      setInitialLoadStatus('Error: ' + error.message);
    }
  }, [fetchNotams]);

  useEffect(() => { fetchNotams(); }, [fetchNotams]);

  // Refresh after live updates, keyed on the newest one. The buffer is capped at
  // 200, so keying on its length stopped refreshes for good after ~an hour and
  // cancelled NOTAMs stayed on screen. Debounced: updates arrive in bursts.
  const latestUpdateAt = notamUpdates[0]?.receivedAt;
  useEffect(() => {
    if (!latestUpdateAt) return;
    const timer = setTimeout(() => fetchNotams({ background: true }), 5000);
    return () => clearTimeout(timer);
  }, [latestUpdateAt, fetchNotams]);

  // ─── Derive available countries & regions from data ───
  const { countries, regions } = useMemo(() => {
    const countrySet = new Map();
    const regionSet = new Map();
    notams.forEach(n => {
      const country = getCountryFromIcao(n.icaoLocation);
      if (country) countrySet.set(country, (countrySet.get(country) || 0) + 1);
      const region = getRegion(n);
      if (region && country === countryFilter) {
        regionSet.set(region, (regionSet.get(region) || 0) + 1);
      }
    });
    // Sort by count descending
    const sortedCountries = [...countrySet.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ name, count }));
    const sortedRegions = [...regionSet.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([name, count]) => ({ name, count }));
    return { countries: sortedCountries, regions: sortedRegions };
  }, [notams, countryFilter]);

  // Countries with sub-regions (show FIR filter)
  const showRegionFilter = countryFilter !== 'ALL' && regions.length > 1;

  // Reset region when country changes
  useEffect(() => { setRegionFilter('ALL'); }, [countryFilter]);

  // ─── Apply all filters ───
  const filteredNotams = useMemo(() => {
    return notams.filter(n => {
      // Category toggle
      const cat = classifyNotam(n);
      if (!categoryToggles[cat]) return false;
      // Search text
      if (searchText.trim()) {
        const s = searchText.toUpperCase().trim();
        if (!(n.notamId || '').toUpperCase().includes(s) &&
            !(n.icaoLocation || '').toUpperCase().includes(s) &&
            !(n.airportName || '').toUpperCase().includes(s) &&
            !(n.notamText || '').toUpperCase().includes(s)) return false;
      }
      // Country
      if (countryFilter !== 'ALL') {
        const country = getCountryFromIcao(n.icaoLocation);
        if (country !== countryFilter) return false;
      }
      // Region / FIR
      if (regionFilter !== 'ALL') {
        if (getRegion(n) !== regionFilter) return false;
      }
      return true;
    });
  }, [notams, searchText, categoryToggles, countryFilter, regionFilter]);

  // NOTAMs that have map coordinates
  const mappableNotams = useMemo(() =>
    filteredNotams.filter(n => n.centerLatitude != null && n.centerLongitude != null),
    [filteredNotams]
  );

  // ─── Toggle a category ───
  const toggleCategory = (cat) => {
    setCategoryToggles(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ─── Select NOTAM and scroll list ───
  const handleSelectNotam = useCallback((notam) => {
    setSelectedNotam(prev => prev?.notamId === notam.notamId ? null : notam);
  }, []);

  // Scroll list to selected card
  useEffect(() => {
    if (selectedNotam && listRef.current) {
      const card = listRef.current.querySelector(`[data-notam-id="${selectedNotam.notamId}"]`);
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [selectedNotam]);


  return (
    <div className="notams-page">
      {/* ─── Header ─── */}
      <div className="notams-header">
        <div className="notams-controls">
          <input
            type="text"
            className="notams-search"
            placeholder="Search ICAO, ID..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div className="notams-filter-group">
            {CLASSIFICATION_FILTERS.map(f => (
              <button
                key={f}
                className={`notams-filter-btn ${classFilter === f ? 'active' : ''}`}
                onClick={() => setClassFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Country dropdown */}
          <select
            className="notams-country-select"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
          >
            <option value="ALL">All Countries</option>
            {countries.map(c => (
              <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
            ))}
          </select>

          {/* Region / FIR dropdown (only when country selected and has multiple FIRs) */}
          {showRegionFilter && (
            <select
              className="notams-country-select notams-region-select"
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
            >
              <option value="ALL">All Regions</option>
              {regions.map(r => (
                <option key={r.name} value={r.name}>{r.name} ({r.count})</option>
              ))}
            </select>
          )}
        </div>
        <div className="notams-header-right">
          {isAdmin && (
            <button
              className="notams-filter-btn notams-load-btn"
              onClick={handleInitialLoad}
              disabled={initialLoadStatus === 'loading'}
              title="Drain JMS queue to seed NOTAM database"
            >
              {initialLoadStatus === 'loading' ? 'Loading...' : 'Load from JMS'}
            </button>
          )}
          {initialLoadStatus && initialLoadStatus !== 'loading' && (
            <span className="notams-load-status">{initialLoadStatus}</span>
          )}
          <div className="notams-count">
            <strong>{filteredNotams.length}</strong> active NOTAM{filteredNotams.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* ─── Content: List + Map ─── */}
      <div className="notams-content">
        {/* Left: NOTAM list */}
        <div className="notams-list-panel">
          {loading ? (
            <div className="notams-loading">Loading NOTAMs...</div>
          ) : filteredNotams.length === 0 ? (
            <div className="notams-empty">No active NOTAMs found</div>
          ) : (
            <div className="notams-list" ref={listRef}>
              {filteredNotams.map(notam => {
                const category = classifyNotam(notam);
                const isSelected = selectedNotam?.notamId === notam.notamId;
                return (
                  <div
                    key={notam.notamId || notam.id}
                    data-notam-id={notam.notamId}
                    className={`notam-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectNotam(notam)}
                  >
                    <div className="notam-card-header">
                      <span className="notam-card-id">{notam.notamId}</span>
                      <span className={`notam-type-badge type-${notam.notamType}`}>
                        {getTypeLabel(notam.notamType)}
                      </span>
                      <span className={`notam-category-badge cat-${category}`}>
                        {NOTAM_COLORS[category].label}
                      </span>
                      <span className="notam-card-location">
                        {notam.icaoLocation}{notam.airportName ? ` (${notam.airportName})` : ''}
                      </span>
                    </div>
                    <div className="notam-card-text">{notam.notamText || '-'}</div>
                    <div className="notam-card-dates">
                      {formatDate(notam.effectiveStart)} — {notam.effectiveEnd ? formatDate(notam.effectiveEnd) : 'PERM'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Map */}
        <div className="notams-map-panel">
          <MapContainer
            center={[40, -30]}
            zoom={3}
            style={{ width: '100%', height: '100%', filter: mapFilter }}
            zoomControl={true}
            attributionControl={false}
          >
            <TileLayer
              url={tileConfig.url || 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'}
              attribution=""
              subdomains={tileConfig.subdomains || ['a', 'b', 'c']}
            />
            <MapFitter notams={mappableNotams} selectedNotam={selectedNotam} filterKey={`${countryFilter}|${regionFilter}`} />

            {/* Render NOTAM shapes and markers */}
            {mappableNotams.map(notam => {
              const category = classifyNotam(notam);
              const colors = NOTAM_COLORS[category];
              const polygon = parsePolygon(notam.polygonCoordinates);
              const isSelected = selectedNotam?.notamId === notam.notamId;
              const hasRadius = notam.radiusNm > 0;
              const elements = [];

              // Polygon
              if (polygon) {
                elements.push(
                  <Polygon
                    key={`poly-${notam.notamId}`}
                    positions={polygon}
                    pathOptions={{
                      color: isSelected ? '#fff' : colors.stroke,
                      fillColor: colors.fill,
                      fillOpacity: isSelected ? 0.30 : 0.15,
                      weight: isSelected ? 3 : 2,
                      dashArray: isSelected ? null : '6 4'
                    }}
                    eventHandlers={{ click: () => handleSelectNotam(notam) }}
                  >
                    <Tooltip sticky>
                      {notam.notamId} — {notam.icaoLocation} {notam.airportName ? `(${notam.airportName})` : ''}
                    </Tooltip>
                  </Polygon>
                );
              }

              // Circle
              if (hasRadius && !polygon) {
                elements.push(
                  <Circle
                    key={`circle-${notam.notamId}`}
                    center={[notam.centerLatitude, notam.centerLongitude]}
                    radius={notam.radiusNm * 1852}
                    pathOptions={{
                      color: isSelected ? '#fff' : colors.stroke,
                      fillColor: colors.fill,
                      fillOpacity: isSelected ? 0.25 : 0.12,
                      weight: isSelected ? 3 : 2,
                      dashArray: isSelected ? null : '6 4'
                    }}
                    eventHandlers={{ click: () => handleSelectNotam(notam) }}
                  >
                    <Tooltip sticky>
                      {notam.notamId} — {notam.icaoLocation} {notam.airportName ? `(${notam.airportName})` : ''}
                    </Tooltip>
                  </Circle>
                );
              }

              // Marker
              elements.push(
                <Marker
                  key={`marker-${notam.notamId}`}
                  position={[notam.centerLatitude, notam.centerLongitude]}
                  icon={createNotamIcon(category, isSelected)}
                  eventHandlers={{ click: () => handleSelectNotam(notam) }}
                >
                  <Tooltip>
                    {notam.notamId} [{NOTAM_COLORS[category].label}] {notam.icaoLocation}
                    {notam.airportName ? ` (${notam.airportName})` : ''}
                  </Tooltip>
                </Marker>
              );

              return elements;
            })}
          </MapContainer>

          {/* Category toggle buttons overlaid on map */}
          <div className="notams-map-toggles">
            {Object.entries(NOTAM_COLORS).map(([cat, info]) => {
              const totalCount = notams.filter(n => classifyNotam(n) === cat).length;
              return (
                <button
                  key={cat}
                  className={`notams-map-toggle ${categoryToggles[cat] ? 'active' : ''}`}
                  onClick={() => toggleCategory(cat)}
                  style={{
                    '--toggle-color': info.fill,
                    '--toggle-stroke': info.stroke,
                  }}
                >
                  <span className="toggle-dot" />
                  <span className="toggle-label">{info.label}</span>
                  <span className="toggle-count">{totalCount}</span>
                </button>
              );
            })}
          </div>

          {/* Detail panel overlaid on map corner */}
          {selectedNotam && (
            <div className="notams-map-detail">
              <button className="notams-detail-close" onClick={() => setSelectedNotam(null)}>×</button>
              <div className="notam-detail-header">
                <span className="notam-detail-id">{selectedNotam.notamId}</span>
                <span className={`notam-type-badge type-${selectedNotam.notamType}`}>
                  {getTypeLabel(selectedNotam.notamType)}
                </span>
                <span className={`notam-category-badge cat-${classifyNotam(selectedNotam)}`}>
                  {NOTAM_COLORS[classifyNotam(selectedNotam)].label}
                </span>
              </div>

              <div className="notam-detail-section">
                <div className="notam-detail-text">{selectedNotam.notamText || '-'}</div>
              </div>

              {selectedNotam.icaoFormatText && (
                <div className="notam-detail-section">
                  <h4>ICAO Format</h4>
                  <div className="notam-detail-text notam-detail-icao">{selectedNotam.icaoFormatText}</div>
                </div>
              )}

              <div className="notam-detail-section">
                <h4>Details</h4>
                <div className="notam-detail-grid">
                  <div className="notam-detail-field">
                    <span className="field-label">Location</span>
                    <span className="field-value">{selectedNotam.icaoLocation || '-'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Airport</span>
                    <span className="field-value">{selectedNotam.airportName || selectedNotam.locationText || '-'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Classification</span>
                    <span className="field-value">{selectedNotam.classification || '-'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">FIR</span>
                    <span className="field-value">{selectedNotam.affectedFir || '-'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Q-Code</span>
                    <span className="field-value">{selectedNotam.selectionCode || '-'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Series / Number</span>
                    <span className="field-value">{selectedNotam.series || '-'} {selectedNotam.number || ''} / {selectedNotam.year || ''}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Effective From</span>
                    <span className="field-value">{formatDate(selectedNotam.effectiveStart)}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Effective Until</span>
                    <span className="field-value">{selectedNotam.effectiveEnd ? formatDate(selectedNotam.effectiveEnd) : 'PERM'}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">Issued</span>
                    <span className="field-value">{formatDate(selectedNotam.issuedAt)}</span>
                  </div>
                  <div className="notam-detail-field">
                    <span className="field-label">FL Range</span>
                    <span className="field-value">
                      {selectedNotam.minimumFl != null ? `FL${selectedNotam.minimumFl}` : '-'} — {selectedNotam.maximumFl != null ? `FL${selectedNotam.maximumFl}` : '-'}
                    </span>
                  </div>
                  {selectedNotam.centerLatitude != null && (
                    <>
                      <div className="notam-detail-field">
                        <span className="field-label">Center</span>
                        <span className="field-value">
                          {selectedNotam.centerLatitude.toFixed(4)}, {selectedNotam.centerLongitude.toFixed(4)}
                        </span>
                      </div>
                      <div className="notam-detail-field">
                        <span className="field-label">Radius</span>
                        <span className="field-value">{selectedNotam.radiusNm ? `${selectedNotam.radiusNm} NM` : '-'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Notams;
